// File: frontend/src/pages/Reinsurance/components/CedentManagement/CedentList/index.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faSearch, faFilter, 
  faExclamationCircle, faSync, faTimes,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useCedent } from '../../../contexts/CedentContext';
import ReinsuranceTable from '../../common/ReinsuranceTable';
import Dropdown from '../../../../../components/form/Dropdown/Dropdown';
import styles from './CedentList.module.css';

const CedentList = ({ onViewCedent, onCreateCedent }) => {
  const { t } = useLanguage();
  const { cedents, fetchCedents, loading, error, clearError } = useCedent();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCedents, setFilteredCedents] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'cedent_name', direction: 'ascending' });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    domicile: '',
    cedentType: ''
  });
  
  // Track if initial data load has been performed
  const initialLoadPerformed = useRef(false);
  // Track if filters have changed since last fetch
  const filtersChanged = useRef(false);
  
  // Prepare dropdown options
  const domicileOptions = [
    { value: '', labelKey: 'all_domiciles' },
    { value: 'Bermuda', labelKey: 'bermuda' },
    { value: 'United States', labelKey: 'united_states' },
    { value: 'Singapore', labelKey: 'singapore' },
    { value: 'Switzerland', labelKey: 'switzerland' },
    { value: 'United Kingdom', labelKey: 'united_kingdom' }
  ];
  
  const cedentTypeOptions = [
    { value: '', labelKey: 'all_types' },
    { value: 'Insurance Company', labelKey: 'insurance_company' },
    { value: 'Reinsurance Company', labelKey: 'reinsurance_company' },
    { value: 'Captive', labelKey: 'captive' },
    { value: 'Lloyd\'s Syndicate', labelKey: 'lloyds_syndicate' }
  ];
  
  // Memoized function to load cedents
  const loadCedents = useCallback(() => {
    clearError();
    
    // Prepare API filters
    const apiFilters = {};
    if (filters.domicile) apiFilters.domicile = filters.domicile;
    if (filters.cedentType) apiFilters.cedentType = filters.cedentType;
    
    // Reset the filters changed flag
    filtersChanged.current = false;
    
    return fetchCedents(apiFilters);
  }, [fetchCedents, clearError, filters.domicile, filters.cedentType]);
  
  // Initial data load - only runs once on mount
  useEffect(() => {
    if (!initialLoadPerformed.current) {
      loadCedents();
      initialLoadPerformed.current = true;
    }
    
    // Cleanup function
    return () => {
      // Any cleanup needed
    };
  }, [loadCedents]);
  
  // Handle filter changes - only fetch when filters actually change
  useEffect(() => {
    if (initialLoadPerformed.current && filtersChanged.current) {
      loadCedents();
    }
  }, [filters, loadCedents]);
  
  // Apply search and sort client-side
  useEffect(() => {
    if (cedents.length > 0) {
      let filtered = [...cedents];
      
      // Apply search
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(cedent => 
          (cedent.cedent_name && cedent.cedent_name.toLowerCase().includes(search)) ||
          (cedent.cedent_domicile && cedent.cedent_domicile.toLowerCase().includes(search))
        );
      }
      
      // Apply sorting
      if (sortConfig.key) {
        filtered.sort((a, b) => {
          let aValue = a[sortConfig.key] || '';
          let bValue = b[sortConfig.key] || '';
          
          if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        });
      }
      
      setFilteredCedents(filtered);
    } else {
      setFilteredCedents([]);
    }
  }, [cedents, searchTerm, sortConfig]);
  
  // Handle sort
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };
  
  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle filter change
  const handleFilterChange = (name, value) => {
    setFilters(prev => {
      // Only mark as changed if the value is actually different
      if (prev[name] !== value) {
        filtersChanged.current = true;
      }
      return { ...prev, [name]: value };
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    // Only trigger a fetch if filters are actually being reset
    if (filters.domicile !== '' || filters.cedentType !== '' || searchTerm !== '') {
      filtersChanged.current = true;
      setFilters({
        domicile: '',
        cedentType: ''
      });
      setSearchTerm('');
    }
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };
  
  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.domicile !== '' || filters.cedentType !== '' || searchTerm !== '';
  };
  
  // Manual refresh function
  const handleRefresh = () => {
    filtersChanged.current = true;
    loadCedents();
  };
  
  // Table columns
  const columns = [
    { id: 'cedent_name', label: 'cedent_name', sortable: true },
    { id: 'cedent_domicile', label: 'domicile', sortable: true },
    { id: 'cedent_type', label: 'type', sortable: true },
    { id: 'active_contracts', label: 'active_contracts', sortable: true },
    { id: 'total_premium', label: 'total_premium', format: 'currency', sortable: true },
    { id: 'am_best_rating', label: 'am_best_rating', sortable: true }
  ];
  
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={handleRefresh}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }
  
  return (
    <div className={styles.cedentList}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('cedent_management')}</h1>
        <button className={styles.createButton} onClick={onCreateCedent}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{t('create_cedent')}</span>
        </button>
      </div>
      
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t('search_cedents')}
            value={searchTerm}
            onChange={handleSearch}
            id="cedent-search"
            name="cedent-search"
          />
          {searchTerm && (
            <button 
              className={styles.clearSearchButton} 
              onClick={clearSearch}
              aria-label={t('clear_search')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        
        <button 
          className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          aria-expanded={showFilters}
          aria-controls="filter-panel"
        >
          <FontAwesomeIcon icon={faFilter} />
          <span>{t('filters')}</span>
        </button>
      </div>
      
      {showFilters && (
        <div className={styles.filtersContainer} id="filter-panel">
          <div className={styles.filterGroup}>
            <label htmlFor="domicile-dropdown">{t('domicile')}</label>
            <Dropdown
              id="domicile-dropdown"
              name="domicile-dropdown"
              options={domicileOptions}
              value={filters.domicile}
              onChange={(value) => handleFilterChange('domicile', value)}
              variant="standalone"
              placeholderKey="all_domiciles"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label htmlFor="cedentType-dropdown">{t('cedent_type')}</label>
            <Dropdown
              id="cedentType-dropdown"
              name="cedentType-dropdown"
              options={cedentTypeOptions}
              value={filters.cedentType}
              onChange={(value) => handleFilterChange('cedentType', value)}
              variant="standalone"
              placeholderKey="all_types"
            />
          </div>
          
          <div className={styles.filterGroup}>
            <label className={styles.resetLabel}>{t('reset_all_filters')}</label>
            <button 
              className={`${styles.resetButton} ${hasActiveFilters() ? styles.active : styles.disabled}`}
              onClick={resetFilters}
              disabled={!hasActiveFilters()}
              id="reset-filters"
              name="reset-filters"
            >
              <FontAwesomeIcon icon={faUndo} className={styles.resetIcon} />
              <span>{t('reset_filters')}</span>
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.tableWrapper}>
        <ReinsuranceTable
          columns={columns}
          data={filteredCedents}
          onRowClick={(row) => onViewCedent(row.cedent_id)}
          emptyMessage="no_cedents_found"
          sortConfig={sortConfig}
          onSort={handleSort}
          isLoading={loading}
          pageSize={10}
        />
      </div>
    </div>
  );
};

export default CedentList;